"use client";

import React from "react";

import Page from "@/components/ui/page";
import Section from "@/components/ui/section";
import { tinaFieldSafe, useTina } from "@/utils";
import Header from "@/components/Header";
import CallToAction from "@/components/CallToAction";
import TiptapContent from "@/components/TiptapContent";
import ScrollSpyLayout, {
  type ScrollSpyItem,
} from "@/components/ui/scroll-spy-layout";
import { useRouter } from "next/navigation";

export default function LegalAndCompliance(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const router = useRouter();

  const header = data?.legalAndCompliance?.header;
  const callToAction = data?.legalAndCompliance?.cta;

  const slugify = (text: string) => {
    return text?.toLowerCase()?.replace(/ /g, "-");
  };

  const sections = data?.legalAndCompliance?.sections ?? [];
  const items: ScrollSpyItem<any>[] = sections.map((section: any) => ({
    id: slugify(section?.title),
    label: section?.title,
    data: section,
  }));

  return (
    <Page
      defaultTinaField={data?.legalAndCompliance}
      seo={data?.legalAndCompliance?.seo}
      pagePath="/legal-and-compliance"
      titleFallback="Legal & Compliance"
    >
      <Header {...header} variant="left" />
      <Section className="relative">
        <ScrollSpyLayout<any>
          items={items}
          scrollOffset={-60}
          onNavItemClick={(item) => {
            const anchor = document.getElementById(item.id);
            if (anchor) {
              router.replace(`/legal-and-compliance#${item.id}`);
            }
          }}
          classNames={{
            root: "grid grid-cols-1 md:grid-cols-[200px_1fr] relative gap-24",
            nav: "md:sticky top-20 z-10 self-start",
            navItem:
              "flex items-center gap-2 border-b py-3 text-sm text-muted-foreground-light",
            content: "space-y-16",
            section:
              "space-y-6 [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_h4]:text-base [&_h5]:text-sm [&_h6]:text-xs [&_a]:text-primary-200",
          }}
          renderNavItem={(item) => item.label}
          renderSection={(item) => {
            const section = item.data;
            return (
              <>
                <h2
                  className="h3 border-b border-gray-700 pb-4"
                  data-tina-field={tinaFieldSafe(section, "title")}
                >
                  {section.title}
                </h2>
                <div data-tina-field={tinaFieldSafe(section, "description")}>
                  {section.description && (
                    <TiptapContent content={section.description} />
                  )}
                </div>
              </>
            );
          }}
        />
      </Section>
      <CallToAction
        {...callToAction}
        variant="left"
        backgroundVariant="dots"
      />
    </Page>
  );
}
